import { useUserIdWithLoading } from '@dreamstack/auth'
import {
  useFormatToCurrencyCutoff,
  useFormatToDecimal,
  useTranslation,
} from '@dreamstack/i18n'
import { ArrowRight } from '@dreamstack/icons/src/icon-components/ArrowRight'
import { getPropertyLocationLabel } from '@dreamstack/real-estate-logic'
import { SimpleDefinitionList } from '@dreamstack/simple-components/src/lib/SimpleDefinitionList'
import { getOrCreateAnonymousId } from '@dreamstack/tracking/src/getOrCreateAnonymousId'
import { trpc } from '@dreamstack/trpc/client'
import { cn } from '@dreamstack/util'
import type { PropertyPreview } from '@dreamstack/web/lib/prisma/properties'
import first from 'lodash/first'
import Image from 'next/image'
import Link from 'next/link'
import { useRouter } from 'next/router'
import type { FunctionComponent } from 'react'
import { GridWithGapsNoCols } from '../layout/MainGrid'
import { SimpleIconButton } from '../layout/SimpleIconButton'
import {
  Css_Text_16_24_tw,
  PropertyHeadingH3,
  PropertyPriceText,
  PropertyRoomAndSqmText,
  PropertySmallText,
} from '../layout/Texts'
import { transformPropstackImageUrl } from './propstack-image-url-transform'
import { useGetPropertyUrl } from './routing/useGetPropertyUrl'

const StyledUnit: FunctionComponent<React.PropsWithChildren<{}>> = ({
  children,
}) => <span className={cn('font-semibold', Css_Text_16_24_tw)}>{children}</span>

export const TopProperty: FunctionComponent<
  React.PropsWithChildren<{
    property: PropertyPreview
  }>
> = ({ property }) => {
  const t = useTranslation()
  const getPropertyUrl = useGetPropertyUrl()
  const formatCurrency = useFormatToCurrencyCutoff()
  const formatDecimal = useFormatToDecimal()
  const { userId } = useUserIdWithLoading()
  const hasImage =
    property.property_images && property.property_images.length > 0
  const router = useRouter()
  const { mutateAsync: trackButtonClickServer } =
    trpc.anonymousTracking.trackEvent.useMutation()
  const locationLabel = getPropertyLocationLabel(property)

  const squareMeterText = property.property_space_value
    ? formatDecimal(property.property_space_value) + ' '
    : '-'
  const count = property.number_of_rooms ?? 0
  const price = property.price ? formatCurrency(property.price) : '-'
  const pricePerSqm = property.price_per_sqm
    ? formatCurrency(property.price_per_sqm) +
      ` EUR / ${t('accentro:squareMeter')}`
    : '-'

  const image = first(property.property_images)
  const fallbackImageUrl =
    'https://images.unsplash.com/photo-1582407947304-fd86f028f716?ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&ixlib=rb-1.2.1&auto=format&fit=crop&w=1459&q=80'
  const imageUrl =
    transformPropstackImageUrl({ url: image?.url, size: 'big' }) ||
    fallbackImageUrl

  const trackClickCallToActionButton = async () => {
    const anonymousUserId = getOrCreateAnonymousId()
    await trackButtonClickServer({
      eventName: 'buttonClick',
      userId,
      anonymousUserId,
      data: {
        buttonAction: 'Immobilien in der Umgebung',
        url: router.asPath,
        additionalInfo: {
          propertyId: property.id,
          city: property.city,
        },
      },
    })
  }
  return (
    <div className={cn('')}>
      <div
        className={cn(
          'h-px-196 md:h-px-274 lg:h-px-480 relative bg-accentroGray-300'
        )}
      >
        {hasImage && (
          <Image
            alt={image?.title ?? 'property image'}
            className={cn('object-cover absolute')}
            src={imageUrl}
            fill
          />
        )}
      </div>
      <PropertyHeadingH3 className={cn('mt-px-24 md:mt-px-40')}>
        {property.title}
      </PropertyHeadingH3>
      <PropertySmallText className={cn('mt-px-8')}>
        {locationLabel}
      </PropertySmallText>
      <div
        className={cn('border-t-2 border-accentroGray-200 mt-px-24 pt-px-32')}
      >
        <div className={cn('grid grid-cols-2 gap-px-16 md:grid-cols-6')}>
          <SimpleDefinitionList
            big
            dt={<PropertyRoomAndSqmText>{count}</PropertyRoomAndSqmText>}
            dd={
              <PropertySmallText>
                {t('accentro:room.rooms', { count })}
              </PropertySmallText>
            }
          />
          <SimpleDefinitionList
            className={cn('md:col-span-2')}
            big
            dt={
              <PropertyRoomAndSqmText>
                {`${squareMeterText} `}
                {squareMeterText !== '-' && (
                  <StyledUnit>{t('accentro:squareMeter')}</StyledUnit>
                )}
              </PropertyRoomAndSqmText>
            }
            dd={
              <PropertySmallText>{t('accentro:livingSpace')}</PropertySmallText>
            }
          />
          <SimpleDefinitionList
            className={cn('md:col-span-2')}
            big
            dt={
              <PropertyPriceText>
                {`${price} `} <StyledUnit>EUR</StyledUnit>
              </PropertyPriceText>
            }
            dd={
              <PropertySmallText className={cn('whitespace-nowrap')}>
                {pricePerSqm}
              </PropertySmallText>
            }
          />
          <GridWithGapsNoCols className={cn('place-items-end md:col-start-6')}>
            <Link
              href={getPropertyUrl({
                id: property.id,
                city: property.city ?? undefined,
              })}
              passHref
              className={cn('')}
            >
              <SimpleIconButton
                aria-label="View property details"
                color="secondary"
                onClick={trackClickCallToActionButton}
              >
                <ArrowRight className={cn('h-px-32 w-px-32')} />
              </SimpleIconButton>
            </Link>
          </GridWithGapsNoCols>
        </div>
      </div>
    </div>
  )
}
